import { FC, useState } from 'react';
import { useDispatch } from 'react-redux';
import { LocalizedText, usePromiseState } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Button, IconName, Stack, Textarea } from '@optimus-web/core';
import { FloatingPanel } from '@mews/b2b-ui';
import { AvailabilityBlock, AvailabilityBlockUpdateParameters } from '@mews-commander/data-access';
import { createUpdateValue } from '@mews-commander/utils';
import { updateAvailabilityBlocks } from '@mews-commander/legacy-entities/src/actions/availabilityBlocks';

interface NotesButtonProps {
    availabilityBlock: AvailabilityBlock;
}

export const NotesButton: FC<NotesButtonProps> = ({ availabilityBlock }) => {
    const { notes, id } = availabilityBlock;

    const dispatch = useDispatch();

    const [opened, setOpened] = useState(false);
    const [newNotes, setNewNotes] = useState(notes);

    const [submitting, handleSubmit] = usePromiseState();

    const onSubmit = async () => {
        const updateValue = createUpdateValue(notes, newNotes);
        if (updateValue) {
            const update: AvailabilityBlockUpdateParameters = { availabilityBlockId: id, notes: updateValue };
            // eslint-disable-next-line
            await dispatch(updateAvailabilityBlocks({ availabilityBlocks: [update] }));
        }
        setOpened(false);
    };

    return (
        <>
            <Button
                icon={!notes ? IconName.PlusSimple : undefined}
                iconPosition="left"
                onClick={() => setOpened(true)}
                counter={notes ? 1 : undefined}
                type="submit"
            >
                <LocalizedText textKey={TextKey.Notes} />
            </Button>
            <FloatingPanel
                active={opened}
                size="small"
                title={<LocalizedText textKey={TextKey.Notes} />}
                onClose={() => setOpened(false)}
                loading={submitting}
                actions={[
                    {
                        children: <LocalizedText textKey={TextKey.Save} />,
                        variant: 'primary',
                        onClick: handleSubmit(onSubmit),
                        type: 'submit',
                    },
                ]}
            >
                <Stack vertical fill>
                    <Textarea
                        autosize
                        autoCollapse={false}
                        size="large"
                        label={<LocalizedText textKey={TextKey.Note} />}
                        name="notes"
                        value={newNotes || ''}
                        onChange={(ev, value) => setNewNotes(value || null)}
                    />
                </Stack>
            </FloatingPanel>
        </>
    );
};
